import React from "react";
import ContactUs from "../../../components/ContactUs.js";
import { Helmet } from "react-helmet-async";

const services = [
  {
    logo_images: "../assets/icons/github-actions/service-1.png",
    title: "Workflow Design",
    description:
      "Author reusable GitHub Actions workflows with matrix builds, concurrency controls, approvals, and conditionals tailored to your release process.",
  },
  {
    logo_images: "../assets/icons/github-actions/service-2.png",
    title: "Reusable Actions & Templates",
    description:
      "Build internal action libraries and workflow templates for security scans, testing, packaging, and deployments to standardize automation across repositories.",
  },
  {
    logo_images: "../assets/icons/github-actions/service-3.webp",
    title: "CI/CD Pipelines",
    description:
      "Implement end-to-end pipelines for container builds, infrastructure changes, and application releases with artifact management and environment promotions.",
  },
  {
    logo_images: "../assets/icons/github-actions/service-4.png",
    title: "Environment & Secrets Management",
    description:
      "Secure GitHub environments with protection rules, required reviewers, secret scanning, OIDC federation, and integration with Vault/KMS solutions.",
  },
  {
    logo_images: "../assets/icons/github-actions/service-5.png",
    title: "Advanced Integrations",
    description:
      "Connect workflows to AWS, Azure, GCP, Kubernetes, ArgoCD, Terraform Cloud, and third-party APIs with curated action sets and governance.",
  },
];

const benefits = [
  "Native automation within your GitHub repositories",
  "Faster developer onboarding with reusable workflow templates",
  "Policy enforcement through protected environments and required approvals",
  "Improved observability with run logs, annotations, and metrics",
  "Seamless integration with GitHub Packages, Codespaces, and Dependabot",
  "Lower operational overhead compared to self-hosted CI platforms",
];

const expertise = [
  "GitHub Advanced Security and Actions specialists",
  "Experience migrating from Jenkins, CircleCI, and Azure DevOps to GitHub",
  "Self-hosted runner design (VM, Kubernetes, ephemeral) with auto-scaling",
  "Security best practices including secret scanning, code scanning, and OIDC",
  "Workflow analytics, cost optimization, and governance implementation",
  "GitOps patterns combining GitHub Actions, ArgoCD, and Terraform",
];

const useCases = [
  {
    title: "Enterprise Workflow Modernization",
    description:
      "Consolidated 200+ heterogeneous pipelines into GitHub Actions with reusable templates, reducing maintenance effort by 60% and improving release velocity.",
  },
  {
    title: "Secure Supply Chain Automation",
    description:
      "Embedded SAST, dependency scanning, and SBOM generation into Actions workflows, enforcing security gates before production deployments.",
  },
  {
    title: "Multi-Cloud Deployment Pipelines",
    description:
      "Built workflows deploying microservices to EKS, AKS, and GKE using OIDC authentication, Helm, and ArgoCD for zero-trust automation.",
  },
  {
    title: "Self-Hosted Runner Platform",
    description:
      "Implemented Kubernetes-based self-hosted runners with auto-scaling, hardened images, and cost controls supporting GPU and specialized workloads.",
  },
];

const reasons = [
  "Deep GitHub ecosystem expertise across Actions, Advanced Security, and Packages",
  "Proven accelerators for workflow templates, internal actions, and governance",
  "Security-first approach with zero-trust runner design and secret management",
  "Comprehensive documentation, enablement, and center-of-excellence programs",
  "Flexible delivery models: advisory, turnkey implementations, and managed services",
  "Real-time monitoring, alerting, and cost optimization for GitHub Actions usage",
];

const scrollToContact = () => {
  document
    .getElementById("contact-section")
    ?.scrollIntoView({ behavior: "smooth" });
};

const GithubActions = () => (
  <>
    <Helmet>
      <meta
        name="description"
        content="Ayanshtech Solutions — GitHub Actions automation for CI/CD workflows, reusable actions, environments, and secure secrets management."
      />
      <meta name="copyright" content="codeglim" />
      <meta
        property="og:title"
        content="GitHub Actions | Workflow Automation Services | Ayanshtech Solutions"
      />
      <meta property="og:site_name" content="Ayanshtech Solutions" />
      <meta
        property="og:url"
        content="http://ayanshtechnology.com/devops-github-actions"
      />
      <meta
        property="og:description"
        content="GitHub Actions workflow, CI/CD, and automation services."
      />
      <meta property="og:type" content="website" />
      <meta
        property="og:image"
        content="http://ayanshtechnology.com/assets/logo.png"
      />
      <link
        rel="canonical"
        href="http://ayanshtechnology.com/devops-github-actions"
      />
      <title>
        GitHub Actions | Workflow Automation Services | Ayanshtech Solutions
      </title>
    </Helmet>

    <div className="flex flex-col items-center justify-center mb-6 p-6">
      <section className="py-5 w-full">
        <div className="flex flex-col lg:flex-row items-center lg:space-x-16">
          <div className="lg:w-3/4 lg:text-left p-4">
            <h1 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4">
              GitHub Actions
            </h1>
            <p className="sm:text-base md:text-lg lg:text-lg mb-3">
              Turn your GitHub repositories into fully automated delivery
              pipelines. We architect GitHub Actions workflows, reusable actions,
              and secure environments to streamline CI/CD and platform
              automation.
            </p>
            <button
              onClick={scrollToContact}
              className="bg-green-600 text-white hover:bg-yellow-500 hover:text-green-700 hover:border hover:border-yellow-500 font-semibold py-2 px-4 rounded-full mt-6"
            >
              Build with GitHub Actions
            </button>
          </div>
          <div className="flex justify-center mb-12 lg:mb-0">
            <img
              src="../assets/images/devops-pages/GitHub-Act-banner.webp"
              alt="GitHub Actions"
              className="w-full h-auto sm:w-3/4 md:w-2/3 lg:w-full object-contain"
            />
          </div>
        </div>
      </section>

      {/* <section className="mb-5 mt-10 p-4 w-full">
        <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
          About GitHub Actions
        </h2>
        <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
          GitHub Actions is GitHub’s native automation platform, enabling teams
          to build, test, and deploy directly from their repositories. With
          YAML-based workflows, reusable actions, and environment protections,
          developers codify every step of the software delivery lifecycle.
        </p>
      </section> */}

      <section className="text-center mb-5 mt-12 w-full">
        <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
          GitHub Actions Services
        </h2>
        <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
          Workflow design, automation patterns, and governance built for
          enterprise software delivery.
        </p>
      </section>
      <div className="rounded-3xl p-6 w-full">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service) => (
            <div
              key={service.title}
              className="relative flex flex-col space-y-4 rounded-3xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
              
            >
               <div className="flex items-center gap-2">
                <div className="w-[60px] h-[60px]  ">
                  <img src={service.logo_images} className="w-full" alt="" />
                </div>
                <h3 className="text-lg font-bold">{service.title}</h3>
              </div>
              <p className="text-lg text-gray-700">{service.description}</p>
            </div>
          ))}
        </div>
      </div>

      <section className="text-center mb-5 mt-20 w-full">
        <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
          Benefits of GitHub Actions
        </h2>
      </section>
      <div className="p-4 w-full">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit) => (
            <div
              key={benefit}
              className="flex items-start space-x-4 rounded-2xl shadow-md p-4 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
            >
              <div className="h-3 w-3 rounded-full bg-blue-600 mt-2 flex-shrink-0" />
              <p className="text-lg text-gray-700">{benefit}</p>
            </div>
          ))}
        </div>
      </div>

      <section className="p-6 sm:px-8 md:px-12 lg:px-16 w-full mt-10 ">
        <div className="text-center mb-8">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            Our GitHub Expertise
          </h2>
        </div>
        <div className="flex justify-center flex-col md:flex-row item-center ">
        <ul className="list-disc pl-8 sm:text-base md:text-lg lg:text-xl max-w-6xl my-auto space-y-3 text-gray-700">
          {expertise.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
        <div className="w-full md:w-1/3 lg:w-[40%]">
          <img src="../assets/images/devops-pages/GitHub-Act-image.webp" alt="" />
        </div>
        </div>
      </section>

      <section className="text-center mb-5 mt-10 w-full">
        <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
          Use Cases
        </h2>
      </section>
      <div className="rounded-3xl p-6 w-full">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
          {useCases.map((useCase) => (
            <div
              key={useCase.title}
              className="relative flex flex-col space-y-4 rounded-3xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
             
            >
              <h3 className="text-lg font-bold text-gray-900">
                {useCase.title}
              </h3>
              <p className="text-lg text-gray-700">{useCase.description}</p>
            </div>
          ))}
        </div>
      </div>

      <section className="text-center mb-5 mt-10 w-full">
        <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
          Why Choose Ayanshtech
        </h2>
      </section>
      <div className="p-6 sm:p-8 md:p-12 lg:p-16 w-full ">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {reasons.map((reason) => (
            <div
              key={reason}
              className="flex items-start space-x-4 rounded-2xl shadow-md p-4 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
            >
              <div className="h-3 w-3 rounded-full bg-green-600 mt-2 flex-shrink-0" />
              <p className="text-lg text-gray-700">{reason}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-8">
          <button
            onClick={scrollToContact}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-full text-lg transition duration-300"
          >
            Launch GitHub Actions Pipelines
          </button>
        </div>
      </div>

      <div id="contact-section" className="w-full mt-12">
        <ContactUs />
      </div>
    </div>
  </>
);

export default GithubActions;

